import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Badge, Button, cn, Segmented, Switch } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ChevronDown, ChevronRight, Maximize2, Minimize2, PauseCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ShopFloorClient,
  type ProductionOrderComponentDTO,
  type ProductionOrderOperationDTO,
  type ShopFloorBoardDTO,
} from "../../api/floor";
import { formatNumber, StationClient, type StationDTO } from "../../api/manufacturing";
import type { ProductionOrderDTO, ProductionOrderStatus } from "../../api/production";
import { isoDate } from "../../api/reporting";
import { NexpimView } from "../../views";
import { ProductionStatusBadge } from "../production/badges";
import { byPosition, isWaiting, processLabel, processRank, StepChip } from "./steps";

type View = "lot" | "process" | "machine";

const VIEW_KEY = "nexpim.floor.view";
const WIDE_KEY = "nexpim.floor.wide";

function remembered<T extends string>(key: string, fallback: T, allowed: readonly T[]): T {
  try {
    const value = window.localStorage.getItem(key);
    return value && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
  } catch {
    return fallback;
  }
}

function remember(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private window or blocked storage: the choice simply is not remembered.
  }
}

/**
 * The shop-floor board: every lot in production, its components and their
 * steps, refreshed on the shop-floor settings' interval.
 *
 * <p>Three ways to read the same answer: by lot (the default, one card per lot
 * in floor order, each opening onto its components and their steps), by
 * process (what is waiting at Cut, Edge, Machining...: the step before has
 * handed it parts it has not done yet), and by machine (the same, per station,
 * for the steps that list it or any machine of its process).</p>
 *
 * <p>TV mode fills the screen with large type, no navigation, and scrolls the
 * lots slowly when they do not fit: the board on the plant's wall. {@code ?tv=1}
 * opens it directly.</p>
 */
export function ShopFloorBoardPage({ tv: tvFromUrl }: { tv: boolean }) {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const navigate = useNavigate();
  const allowed = hasView(NexpimView.SHOP_FLOOR);
  const floor = useMemo(() => new ShopFloorClient(http), [http]);
  const [view, setView] = useState<View>(() => remembered<View>(VIEW_KEY, "lot", ["lot", "process", "machine"]));
  const [wide, setWide] = useState(() => remembered(WIDE_KEY, "no", ["yes", "no"]) === "yes");
  const [tv, setTv] = useState(tvFromUrl);
  const statuses: ProductionOrderStatus[] = wide ? ["RELEASED", "PRODUCED", "SHIPPING"] : ["RELEASED", "PRODUCED"];

  const board = useQuery({
    queryKey: ["production", "shop-floor", "board", statuses],
    queryFn: () => floor.board(statuses),
    enabled: allowed,
    refetchInterval: (query) => (query.state.data?.refresh_seconds ?? 60) * 1000,
    refetchIntervalInBackground: true,
  });
  const stations = useQuery({
    queryKey: ["station", "all"],
    queryFn: () => new StationClient(http).findAll(),
    enabled: allowed && view === "machine",
    staleTime: 10 * 60_000,
  });

  const setTvMode = (on: boolean) => {
    setTv(on);
    void navigate({ to: "/production/floor", search: on ? { tv: 1 } : {}, replace: true });
    try {
      if (on && !document.fullscreenElement) void document.documentElement.requestFullscreen?.().catch(() => undefined);
      if (!on && document.fullscreenElement) void document.exitFullscreen?.().catch(() => undefined);
    } catch {
      // Fullscreen refused (iframe, policy): the layout still fills the window.
    }
  };

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.shop-floor", "Shop floor")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">{t("error.access-denied.detail", "Your account does not have permission to view this page.")}</p>
        </div>
      </>
    );
  }

  const data = board.data;
  const controls = (
    <div className="flex flex-wrap items-center gap-3">
      <Segmented
        value={view}
        onChange={(next) => {
          setView(next);
          remember(VIEW_KEY, next);
        }}
        options={[
          { value: "lot", label: t("floor.view.lot", "By lot") },
          { value: "process", label: t("floor.view.process", "By process") },
          { value: "machine", label: t("floor.view.machine", "By machine") },
        ]}
      />
      <Switch
        checked={wide}
        onChange={(on) => {
          setWide(on);
          remember(WIDE_KEY, on ? "yes" : "no");
        }}
        label={t("floor.include-shipping", "Include in transit and packing")}
      />
      {data && (
        <span className="text-[12px] text-ink-muted">
          {t("floor.as-of", "As of {{time}}", { time: new Date(data.as_of).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })}
        </span>
      )}
    </div>
  );

  const body = !data ? (
    <p className="p-4 text-[13px] text-ink-muted lg:p-6">{board.isError ? t("error.generic", "Something went wrong.") : t("common.loading", "Loading…")}</p>
  ) : data.lots.length === 0 ? (
    <p className="p-4 text-[14px] text-ink-muted lg:p-6">{t("floor.empty", "No lot is in production.")}</p>
  ) : view === "lot" ? (
    <LotView data={data} tv={tv} />
  ) : view === "process" ? (
    <ProcessView data={data} tv={tv} />
  ) : (
    <MachineView data={data} stations={stations.data ?? []} tv={tv} />
  );

  if (tv) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-surface text-ink">
        <div className="flex flex-wrap items-center gap-4 border-b border-line px-6 py-3">
          <h1 className="text-[26px] font-semibold">{t("navigation.item.shop-floor", "Shop floor")}</h1>
          {controls}
          <Button variant="ghost" className="ml-auto" onClick={() => setTvMode(false)}>
            <Minimize2 className="size-4" />
            {t("floor.tv.exit", "Exit TV mode")}
          </Button>
        </div>
        <AutoScroll className="min-h-0 flex-1">{body}</AutoScroll>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.shop-floor", "Shop floor")}
        subtitle={t("floor.subtitle", "Every lot in production, its components and the steps they have been through.")}
        actions={
          <Button variant="secondary" onClick={() => setTvMode(true)}>
            <Maximize2 className="size-4" />
            {t("floor.tv", "TV mode")}
          </Button>
        }
      />
      <div className="border-b border-line bg-surface px-4 py-2 lg:px-6">{controls}</div>
      <div className="min-h-0 flex-1 overflow-auto">{body}</div>
    </>
  );
}

/** Scrolls its content slowly when it overflows, pausing at the bottom, then starts again: a wall board nobody touches. */
function AutoScroll({ className, children }: { className?: string; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let pause = 0;
    const timer = window.setInterval(() => {
      const box = ref.current;
      if (!box || box.scrollHeight <= box.clientHeight + 2) return;
      if (pause > 0) {
        pause -= 1;
        if (pause === 0 && box.scrollTop + box.clientHeight >= box.scrollHeight - 2) box.scrollTop = 0;
        return;
      }
      box.scrollTop += 1;
      if (box.scrollTop + box.clientHeight >= box.scrollHeight - 2) pause = 80;
    }, 40);
    return () => window.clearInterval(timer);
  }, []);
  return (
    <div ref={ref} className={cn("overflow-auto", className)}>
      {children}
    </div>
  );
}

function stepsByComponent(data: ShopFloorBoardDTO) {
  const byComponent = new Map<number, ProductionOrderOperationDTO[]>();
  const lotLevel = new Map<number, ProductionOrderOperationDTO[]>();
  for (const step of data.operations) {
    if (step.production_order_component_id == null) {
      lotLevel.set(step.production_order_id, [...(lotLevel.get(step.production_order_id) ?? []), step]);
    } else {
      byComponent.set(step.production_order_component_id, [...(byComponent.get(step.production_order_component_id) ?? []), step]);
    }
  }
  return { byComponent, lotLevel };
}

function LotView({ data, tv }: { data: ShopFloorBoardDTO; tv: boolean }) {
  const { byComponent, lotLevel } = useMemo(() => stepsByComponent(data), [data]);
  const componentsOf = useMemo(() => {
    const map = new Map<number, ProductionOrderComponentDTO[]>();
    for (const component of data.components) {
      map.set(component.production_order_id, [...(map.get(component.production_order_id) ?? []), component]);
    }
    return map;
  }, [data]);
  return (
    <div className={cn("flex flex-col", tv ? "gap-4 p-6" : "gap-3 p-4 lg:p-6")}>
      {data.lots.map((lot) => (
        <LotCard
          key={lot.id}
          lot={lot}
          components={componentsOf.get(lot.id) ?? []}
          steps={byComponent}
          packing={lotLevel.get(lot.id) ?? []}
          tv={tv}
        />
      ))}
    </div>
  );
}

function LotCard({
  lot,
  components,
  steps,
  packing,
  tv,
}: {
  lot: ProductionOrderDTO;
  components: ProductionOrderComponentDTO[];
  steps: Map<number, ProductionOrderOperationDTO[]>;
  packing: ProductionOrderOperationDTO[];
  tv: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // On the wall every lot is open; at a desk they start folded.
  const [open, setOpen] = useState(tv);
  useEffect(() => setOpen(tv), [tv]);
  const progress = Math.round(Number(lot.progress ?? 0) * 100);
  const late = lot.due_date != null && lot.due_date < isoDate(new Date());
  const stalled = components.some((component) => (steps.get(component.id) ?? []).some((step) => step.is_stalled));
  const missing = Number(lot.missing_count ?? 0);
  const title = lot.part_number ? `${lot.part_number} · ${lot.part_name ?? ""}` : (lot.name ?? "");

  return (
    <section className={cn("rounded-lg border bg-surface", lot.is_on_hold ? "border-warn/60 opacity-75" : "border-line")}>
      <header className={cn("flex flex-wrap items-center gap-x-4 gap-y-2", tv ? "px-5 py-4" : "px-4 py-3")}>
        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? t("floor.collapse", "Fold") : t("floor.expand", "Open")}
          onClick={() => setOpen(!open)}
          className="flex size-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-sunk"
        >
          {open ? <ChevronDown className="size-5" /> : <ChevronRight className="size-5" />}
        </button>
        <button
          type="button"
          onClick={() => void navigate({ to: "/production/orders/$orderId", params: { orderId: String(lot.id) } })}
          className={cn("font-semibold tabular-nums text-accent hover:underline", tv ? "text-[26px]" : "text-[17px]")}
        >
          {lot.number}
        </button>
        <span className={cn("min-w-[8rem] flex-1 truncate", tv ? "text-[20px]" : "text-[14px]")}>{title}</span>
        <span className={cn("tabular-nums text-ink-muted", tv ? "text-[18px]" : "text-[13px]")}>
          {t("floor.qty", "Qty {{n}}", { n: formatNumber(lot.quantity) })}
        </span>
        {lot.due_date && (
          <span className={cn("tabular-nums", late ? "font-semibold text-risk" : "text-ink-muted", tv ? "text-[18px]" : "text-[13px]")}>
            {t("floor.due", "Due {{date}}", { date: lot.due_date })}
          </span>
        )}
        {lot.priority != null && (
          <Badge variant="accent" size={tv ? "md" : "sm"}>
            {t("floor.priority", "Priority {{n}}", { n: lot.priority })}
          </Badge>
        )}
        <ProductionStatusBadge status={lot.status} />
        {lot.is_on_hold && (
          <Badge variant="warning" size={tv ? "md" : "sm"}>
            <PauseCircle className="mr-1 size-3" />
            {t("floor.on-hold", "On hold")}
          </Badge>
        )}
        {missing > 0 && (
          <Badge variant="error" size={tv ? "md" : "sm"}>
            {t("floor.components-missing", "{{n}} with missing parts", { n: missing })}
          </Badge>
        )}
        {stalled && (
          <Badge variant="warning" size={tv ? "md" : "sm"}>
            <AlertTriangle className="mr-1 size-3" />
            {t("floor.stalled", "Stalled")}
          </Badge>
        )}
        <div className="flex w-full items-center gap-3 sm:w-56">
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-surface-sunk" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
            <div className={cn("h-full rounded-full", progress >= 100 ? "bg-ok" : "bg-accent")} style={{ width: `${String(progress)}%` }} />
          </div>
          <span className={cn("w-12 text-right font-semibold tabular-nums", tv ? "text-[18px]" : "text-[13px]")}>{progress}%</span>
        </div>
      </header>
      {open && (
        <div className="border-t border-line">
          <table className="w-full">
            <tbody>
              {components.map((component) => {
                const nested = (component.parts_per_cutboard ?? 1) > 1;
                return (
                  <tr key={component.id} className="border-b border-line last:border-b-0">
                    <td className={cn("whitespace-nowrap pl-4 pr-3 align-middle font-medium tabular-nums", tv ? "py-2.5 text-[18px]" : "py-1.5 text-[13px]")}>
                      {component.code}
                    </td>
                    <td className={cn("pr-3 align-middle font-semibold", tv ? "text-[20px]" : "text-[14px]")}>{component.letter ?? ""}</td>
                    <td className={cn("w-[34%] max-w-0 pr-3 align-middle", tv ? "text-[16px]" : "text-[12px]")}>
                      <div className="truncate whitespace-nowrap" title={`${component.part_number ?? ""} ${component.part_name ?? ""}`}>
                        <span className="font-medium">{component.part_number}</span>
                        <span className="ml-2 hidden text-ink-muted md:inline">{component.part_name}</span>
                      </div>
                    </td>
                    <td className={cn("whitespace-nowrap pr-3 text-right align-middle tabular-nums", tv ? "text-[16px]" : "text-[12px]")}>
                      {formatNumber(component.quantity)}
                      {nested && (
                        <span className="ml-1 text-ink-faint">
                          ({t("floor.boards-n", "{{n}} boards", { n: formatNumber(component.cutboard_quantity) })})
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-4 align-middle">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {byPosition(steps.get(component.id) ?? []).map((step) => (
                          <StepChip key={step.id} step={step} large={tv} />
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {packing.length > 0 && (
                <tr>
                  <td colSpan={4} className={cn("pl-4 pr-3 text-ink-muted", tv ? "py-2.5 text-[16px]" : "py-1.5 text-[12px]")}>
                    {t("floor.lot-steps", "Whole lot")}
                  </td>
                  <td className="py-1.5 pr-4">
                    <div className="flex flex-wrap gap-1.5">
                      {packing.map((step) => (
                        <StepChip key={step.id} step={step} large={tv} />
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** One waiting step on a process or machine column. */
function WaitingRow({ step, component, tv }: { step: ProductionOrderOperationDTO; component?: ProductionOrderComponentDTO; tv: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const done = Number(step.quantity_done ?? 0);
  const required = Number(step.quantity_required_counted ?? step.quantity_required);
  return (
    <li>
      <button
        type="button"
        onClick={() => void navigate({ to: "/production/orders/$orderId", params: { orderId: String(step.production_order_id) } })}
        className={cn(
          "flex w-full items-center gap-2 rounded-md border px-3 text-left hover:bg-surface-sunk",
          step.is_stalled ? "border-warn/60" : Number(step.quantity_missing ?? 0) > 0 ? "border-risk/50" : "border-line",
          tv ? "py-2.5 text-[17px]" : "py-1.5 text-[12px]",
        )}
      >
        <span className="font-semibold tabular-nums">{step.component_code}</span>
        <span className="font-semibold">{step.letter ?? ""}</span>
        <span className="min-w-0 flex-1 truncate text-ink-muted">{step.part_number}</span>
        <span className="tabular-nums">
          {formatNumber(done)}/{formatNumber(required)}
          {step.counting_unit === "CUTBOARD" && (component?.parts_per_cutboard ?? 1) > 1 && (
            <span className="ml-1 text-ink-faint">{t("floor.cutboards-short", "boards")}</span>
          )}
        </span>
      </button>
    </li>
  );
}

function ProcessView({ data, tv }: { data: ShopFloorBoardDTO; tv: boolean }) {
  const { t } = useTranslation();
  const components = useMemo(() => new Map(data.components.map((component) => [component.id, component])), [data]);
  const columns = useMemo(() => {
    const map = new Map<string, { name: string; steps: ProductionOrderOperationDTO[] }>();
    for (const step of data.operations) {
      if (step.production_order_component_id == null) continue;
      const code = step.process_code ?? String(step.process_id);
      const column = map.get(code) ?? { name: processLabel(t, step.process_code, step.process_name), steps: [] };
      if (isWaiting(step, components.get(step.production_order_component_id))) column.steps.push(step);
      map.set(code, column);
    }
    return [...map.entries()].sort(([a], [b]) => processRank(a) - processRank(b));
  }, [data, components, t]);
  return (
    <div className={cn("grid gap-3", tv ? "p-6" : "p-4 lg:p-6")} style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${tv ? "320px" : "240px"}, 1fr))` }}>
      {columns.map(([code, column]) => (
        <section key={code} className="flex min-h-0 flex-col rounded-lg border border-line bg-surface">
          <header className={cn("flex items-baseline justify-between border-b border-line px-3", tv ? "py-3" : "py-2")}>
            <h2 className={cn("font-semibold", tv ? "text-[22px]" : "text-[14px]")}>{column.name}</h2>
            <span className={cn("tabular-nums text-ink-muted", tv ? "text-[18px]" : "text-[12px]")}>
              {t("floor.waiting-n", "{{n}} waiting", { n: column.steps.length })}
            </span>
          </header>
          <ul className="flex flex-col gap-1.5 p-2">
            {column.steps.length === 0 ? (
              <li className="px-2 py-3 text-[12px] text-ink-faint">{t("floor.nothing-waiting", "Nothing waiting.")}</li>
            ) : (
              column.steps.map((step) => (
                <WaitingRow key={step.id} step={step} component={components.get(step.production_order_component_id ?? 0)} tv={tv} />
              ))
            )}
          </ul>
        </section>
      ))}
    </div>
  );
}

function MachineView({ data, stations, tv }: { data: ShopFloorBoardDTO; stations: StationDTO[]; tv: boolean }) {
  const { t } = useTranslation();
  const components = useMemo(() => new Map(data.components.map((component) => [component.id, component])), [data]);
  const columns = useMemo(() => {
    const processOf = new Map<number, string | null | undefined>();
    for (const step of data.operations) processOf.set(step.process_id, step.process_code);
    return stations
      .filter((station) => station.is_active && station.process_id != null)
      .map((station) => ({
        station,
        code: processOf.get(station.process_id!) ?? null,
        steps: data.operations.filter(
          (step) =>
            step.production_order_component_id != null &&
            step.process_id === station.process_id &&
            (step.station_ids.length === 0 || step.station_ids.includes(station.id)) &&
            isWaiting(step, components.get(step.production_order_component_id)),
        ),
      }))
      .sort((a, b) => processRank(a.code) - processRank(b.code) || a.station.code.localeCompare(b.station.code));
  }, [data, stations, components]);
  return (
    <div className={cn("grid gap-3", tv ? "p-6" : "p-4 lg:p-6")} style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${tv ? "300px" : "220px"}, 1fr))` }}>
      {columns.map(({ station, code, steps }) => (
        <section key={station.id} className="flex flex-col rounded-lg border border-line bg-surface">
          <header className={cn("border-b border-line px-3", tv ? "py-3" : "py-2")}>
            <h2 className={cn("font-semibold", tv ? "text-[21px]" : "text-[14px]")}>{station.name}</h2>
            <p className={cn("text-ink-muted", tv ? "text-[15px]" : "text-[11px]")}>
              {processLabel(t, code)} · {t("floor.waiting-n", "{{n}} waiting", { n: steps.length })}
            </p>
          </header>
          <ul className="flex flex-col gap-1.5 p-2">
            {steps.length === 0 ? (
              <li className="px-2 py-3 text-[12px] text-ink-faint">{t("floor.nothing-waiting", "Nothing waiting.")}</li>
            ) : (
              steps.map((step) => <WaitingRow key={step.id} step={step} component={components.get(step.production_order_component_id ?? 0)} tv={tv} />)
            )}
          </ul>
        </section>
      ))}
    </div>
  );
}
